import { effect, inject, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { AppError, SpinnerService, WINDOW } from '@hglobal/common';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { asyncScheduler, switchMap } from 'rxjs';

import { Account, GeoLocationForApi, UserStatus } from '@pp/core/entities';
import { FormValidationService, GlobalToastService } from '@pp/core/services';
import {
  setErrorStatusFor,
  setPendingStatusFor,
  setSuccessStatusFor,
  StorageStore,
  withRequestHandler,
  withRequestStatus,
} from '@pp/core/store';
import { DictionariesStore } from '@pp/dictionaries';

import { AUTH_API } from './api';
import { AUTH_KEY, NEED_VERIFY_PHONE_STATUS_CODE, STATUS, TWO_FA_REQUIRED_STATUS_CODE } from './auth.constants';
import { InviteTokenStatus, logOutCodesCollection, logOutErrorToastData, RegistrationStatus, TwoFactorStatus } from './auth.entities';
import {
  AuthorizationReq,
  AuthorizationRes,
  AuthState,
  ChangeEmailPhoneReq,
  PasswordRecoveryReq,
  SetDataReq,
  SetPhoneReq,
  UserAcceptInviteReq,
  VerifyEmailPhoneReq,
} from './model';

const initialState: AuthState = {
  is2faEnabled: false,
  isActive: false,
  isAuthorized: false,
  user: null,
  isPreload: false,
  qrCode: '',
  secret: '',
  address: null,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus([
    'signUp',
    'checkInviteToken',
    'whoAmI',
    'signIn',
    'setPhone',
    'changePhone',
    'setData',
    'verifyEmail',
    'resendEmailVerificationCode',
    'verifyPhone',
    'setTfa',
    'resendPhone',
    'recoveryEmail',
    'recoveryPassword',
    'activate2fa',
    'changeEmailPhone',
    'verifyEmailPhone',
    'setAddress',
  ]),
  withRequestHandler(),
  withProps(store => ({
    store,
    authApi: inject(AUTH_API),
    dictionariesStore: inject(DictionariesStore),
    globalToastService: inject(GlobalToastService),
    router: inject(Router),
    window: inject<Window>(WINDOW),
    storage: inject(StorageStore),
    formValidationService: inject(FormValidationService),
    spinnerService: inject(SpinnerService),
  })),
  withMethods(({ store, router, storage, formValidationService, window, dictionariesStore }) => ({
    setAuthStatus: (isAuthorized: boolean) => patchState(store, { isAuthorized }),
    setActivationStatus: (isActive: boolean) => patchState(store, { isActive }),
    navigateToPhoneConfirmation: (phone: string) => {
      router.navigate(['signup/code'], {
        state: { phone: formValidationService.cleanPhoneNumber(phone) },
      });
    },
    logout: () => {
      const { pathname } = window.location;
      const path = pathname.includes('signin') ? '' : pathname;
      window.localStorage.removeItem(AUTH_KEY);
      window.localStorage.removeItem(STATUS);
      router.navigate(['/signin'], { queryParams: { url: !path || path === '/' ? null : encodeURIComponent(path) } });
      patchState(store, { isActive: false, user: undefined });
      asyncScheduler.schedule(() => storage.onLogout(), 500);
      dictionariesStore.clearRole();
    },
  })),
  withMethods(({ store, authApi, globalToastService, router, navigateToPhoneConfirmation, setAuthStatus }) => ({
    checkInviteToken: rxMethod<{ token: string }>(
      switchMap(({ token }) => {
        patchState(store, setPendingStatusFor('checkInviteToken'));
        return authApi.checkInviteToken(token).pipe(
          tapResponse({
            next: res => {
              patchState(store, setSuccessStatusFor('checkInviteToken'));
              res === InviteTokenStatus.Registered ? router.navigate(['/signin']) : patchState(store, { isPreload: false });
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('checkInviteToken', error));
              router.navigate(['/404']);
              globalToastService.showError(error?.originalError?.error);
            },
          }),
        );
      }),
    ),
    signUp: rxMethod<UserAcceptInviteReq>(
      switchMap(data => {
        patchState(store, setPendingStatusFor('signUp', false));
        return authApi.signUp(data).pipe(
          tapResponse({
            next: payload => {
              setSuccessStatusFor('signUp', false);
              handleSuccessSignUp(payload, router);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('signUp', error, false));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    signIn: rxMethod<Pick<AuthorizationReq, 'login' | 'password' | 'code'>>(
      switchMap(({ login, password, code }) => {
        patchState(store, setPendingStatusFor('signIn'));
        return authApi.signIn({ login, password, code }).pipe(
          tapResponse({
            next: payload => {
              patchState(store, setSuccessStatusFor('signIn'));
              if (payload?.code === TWO_FA_REQUIRED_STATUS_CODE || payload === null) {
                router.navigate(['signin/tfa'], { state: { signinForm: { login, password, code } } });
              } else {
                const { account } = payload;
                patchState(store, {
                  is2faEnabled: account.twoFactorStatus === TwoFactorStatus.Enable,
                });
                handleAccountStatus(account, payload, password, router, setAuthStatus, globalToastService);
              }
              if (payload !== null && payload.code !== TWO_FA_REQUIRED_STATUS_CODE) {
                const { key, account } = payload;
                patchState(store, { user: account || store.user() });
                if (account?.status === UserStatus.Active && account?.twoFactorStatus === TwoFactorStatus.Enable) {
                  patchState(store, { isActive: true });
                  window.localStorage.setItem(STATUS, account?.status.toString());
                  setAuthStatus(true);
                  router.navigate(['/']);
                }
                window.localStorage.setItem(AUTH_KEY, key);
              }
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('signIn', error));
              if (error?.originalError.error.error_description !== '2FA wrong code') {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
    setPhone: rxMethod<SetPhoneReq>(
      switchMap(phone => {
        patchState(store, setPendingStatusFor('setPhone'));
        return authApi.setPhone(phone).pipe(
          tapResponse({
            next: ({ code }) => {
              patchState(store, setSuccessStatusFor('setPhone'));
              if (code === NEED_VERIFY_PHONE_STATUS_CODE) {
                navigateToPhoneConfirmation(phone.phone);
              }
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('setPhone', error));
              if (getErrorCode(error) !== '1009') {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
    changePhone: rxMethod<SetPhoneReq>(
      switchMap(phone => {
        patchState(store, setPendingStatusFor('changePhone'));
        return authApi.changePhone(phone).pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('changePhone'));
              navigateToPhoneConfirmation(phone.phone);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('changePhone', error));
              if (getErrorCode(error) !== '1009') {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
    resendPhone: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('resendPhone'));
        return authApi.resendPhone().pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('resendPhone'));
              globalToastService.showSuccess(status);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('resendPhone', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    resendEmailVerificationCode: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('resendEmailVerificationCode'));
        return authApi.resendEmailVerificationCode().pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('resendEmailVerificationCode')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('resendEmailVerificationCode', error)),
          }),
        );
      }),
    ),
    setData: rxMethod<SetDataReq>(
      switchMap(({ name, surname }) => {
        patchState(store, setPendingStatusFor('setData'));
        return authApi.setData({ name, surname }).pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('setData'));
              asyncScheduler.schedule(() => {
                if (status === 'Data was successfully saved') {
                  router.navigate(['signin']);
                }
              }, 500);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('setData', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    setTfa: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('setTfa'));
        return authApi.setTfa().pipe(
          tapResponse({
            next: ({ image, secret }) => {
              patchState(store, { qrCode: 'data:image/png;base64,' + image, secret }, setSuccessStatusFor('setTfa'));
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('setTfa', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    activate2fa: rxMethod<{ code: string }>(
      switchMap(({ code }) => {
        patchState(store, setPendingStatusFor('activate2fa'));
        return authApi.activateTfa(code).pipe(
          tapResponse({
            next: () => {
              patchState(store, setSuccessStatusFor('activate2fa'));
              router.navigate(['/']);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('activate2fa', error));
              if (error?.code !== 'error400') {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
    recoveryEmail: rxMethod<{ email: string }>(
      switchMap(({ email }) => {
        patchState(store, setPendingStatusFor('recoveryEmail'));
        return authApi.recoveryEmail(email).pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('recoveryEmail'));
              if (status) {
                globalToastService.showSuccess(status);
              }
              router.navigate(['/signin']);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('recoveryEmail', error));
              if (error.code !== 'error400') {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
    recoveryPassword: rxMethod<PasswordRecoveryReq>(
      switchMap(({ password, token }) => {
        patchState(store, setPendingStatusFor('recoveryPassword'));
        return authApi.recoveryPassword({ password, token }).pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('recoveryPassword'));
              if (status) {
                globalToastService.showSuccess(status);
              }
              router.navigate(['/signin']);
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('recoveryPassword', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    changeEmailPhone: rxMethod<ChangeEmailPhoneReq>(
      switchMap(({ phone, email }) => {
        patchState(store, setPendingStatusFor('changeEmailPhone'));
        return authApi.changeEmailPhone({ phone, email }).pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('changeEmailPhone')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('changeEmailPhone', error)),
          }),
        );
      }),
    ),
    verifyEmailPhone: rxMethod<VerifyEmailPhoneReq>(
      switchMap(({ code_email, code_phone }) => {
        patchState(store, setPendingStatusFor('verifyEmailPhone'));
        return authApi.verifyEmailPhone({ code_email, code_phone }).pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('verifyEmailPhone')),
            error: (error: AppError) => patchState(store, setErrorStatusFor('verifyEmailPhone', error)),
          }),
        );
      }),
    ),
    setAddress: rxMethod<GeoLocationForApi>(
      switchMap(address => {
        patchState(store, { address }, setPendingStatusFor('setAddress'));
        return authApi.setAddress(address).pipe(
          tapResponse({
            next: () => patchState(store, setSuccessStatusFor('setAddress')),
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('setAddress', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
  })),
  withMethods(({ store, authApi, setTfa, storage, globalToastService, router }) => ({
    whoAmI: rxMethod<void>(
      switchMap(() => {
        patchState(store, setPendingStatusFor('whoAmI'));
        return authApi.whoAmI().pipe(
          tapResponse({
            next: account => {
              patchState(store, { user: account }, setSuccessStatusFor('whoAmI'));
              storage.readonlyRightsSet(account.hasRestrictedAccess);
              if (account.status && account.status === RegistrationStatus.WaitingForPhoneApprove) {
                router.navigate(['signup/phone']);
              } else if (account.status === RegistrationStatus.WaitingForEnterData) {
                router.navigate(['signup/data']);
              } else if (
                account.status === RegistrationStatus.Active &&
                (account.twoFactorStatus === TwoFactorStatus.WaitForApprove || account.twoFactorStatus === TwoFactorStatus.Disable)
              ) {
                setTfa();
              }
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('whoAmI', error));
              if (logOutCodesCollection.has(getErrorCode(error))) {
                globalToastService.showCustom(logOutErrorToastData);
              } else {
                globalToastService.showError(getErrorMessage(error));
              }
            },
          }),
        );
      }),
    ),
  })),
  withMethods(({ store, authApi, whoAmI, globalToastService }) => ({
    verifyEmail: rxMethod<{ code: string }>(
      switchMap(({ code }) => {
        patchState(store, setPendingStatusFor('verifyEmail'));
        return authApi.verifyEmail(code).pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('verifyEmail'));
              if (status && status === 'Email was successful verified') {
                whoAmI();
              }
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('verifyEmail', error));
              globalToastService.showError(getErrorMessage(error));
            },
          }),
        );
      }),
    ),
    verifyPhone: rxMethod<{ code: string }>(
      switchMap(({ code }) => {
        patchState(store, setPendingStatusFor('verifyPhone'));
        return authApi.verifyPhone(code).pipe(
          tapResponse({
            next: ({ status }) => {
              patchState(store, setSuccessStatusFor('verifyPhone'));
              if (status === 'Phone was successful verified') {
                whoAmI();
              }
            },
            error: (error: AppError) => {
              patchState(store, setErrorStatusFor('verifyPhone', error));
              const message = getErrorCode(error) === '1008' ? 'signin.tfaEnable.error.code' : error.message;
              globalToastService.showError(message);
            },
          }),
        );
      }),
    ),
  })),
  withMethods(store => ({
    initialize(): void {
      if (window.localStorage.getItem(AUTH_KEY)) {
        store.whoAmI();
      }
    },
  })),
  withHooks({
    onInit(store) {
      store.initialize();
      store.spinnerService.observe(toObservable(store.isLoading));
      effect(() => {
        store.dictionariesStore.setUser(store.user());
      });

      effect(() => {
        if (store.isActive()) {
          untracked(() => {
            store.storage.loadAccessRights();
            store.storage.loadOrganizations();
          });
        }
      });
    },
  }),
);

function getErrorCode(error: any): string | undefined {
  return error?.originalError?.error?.error_code;
}

function getErrorMessage(error: any): string {
  return error?.originalError?.error?.error_description ?? error?.originalError?.error?.errorDescription ?? error.message;
}

function handleSuccessSignUp(payload: AuthorizationRes, router: Router) {
  if (payload === null) {
    router.navigate(['signip/tfa-set']);
  } else {
    const { account, key } = payload;
    window.localStorage.setItem(AUTH_KEY, key);
    switch (account?.status) {
      case RegistrationStatus.WaitingForEmailApprove: {
        router.navigate(['signup/email-code'], {
          state: { email: account.login },
        });
        break;
      }
      case RegistrationStatus.WaitingForPhoneApprove: {
        router.navigate(['signup/phone'], { state: { phone: account.phone } });
        break;
      }
      case RegistrationStatus.WaitingForEnterData: {
        router.navigate(['signup/data']);
        break;
      }
    }
  }
}

function handleAccountStatus(
  account: Account,
  payload: AuthorizationRes,
  password: string,
  router: Router,
  setAuthStatus: (status: boolean) => void,
  globalToastService: GlobalToastService,
) {
  switch (account.status) {
    case RegistrationStatus.WaitingForEmailApprove: {
      router.navigate(['signup/email-code'], {
        state: { email: account.email, password },
      });
      break;
    }
    case RegistrationStatus.WaitingForPhoneApprove: {
      router.navigate(['signup/phone'], { state: { phone: account.phone } });
      break;
    }
    case RegistrationStatus.WaitingForEnterData: {
      router.navigate(['signup/data']);
      break;
    }
    case RegistrationStatus.Active: {
      if (payload.account.twoFactorStatus === TwoFactorStatus.Enable) {
        setAuthStatus(true);
        router.navigate(['/']);
      }
      if (account.twoFactorStatus === TwoFactorStatus.Disable || account.twoFactorStatus === TwoFactorStatus.WaitForApprove) {
        router.navigate(['signin/tfa-set']);
      } else {
        router.navigate(['signin/tfa']);
      }
      break;
    }
    case RegistrationStatus.WaitingForAccountApprove: {
      globalToastService.showInfo('signup.status.waitingForApprove');
      break;
    }
  }
}
